import React, { useState, useCallback } from 'react';
import { Upload, FileText, X, Check, AlertCircle, Loader } from 'lucide-react';

/**
 * Drag and Drop File Uploader Component
 * Supports resume and job description document uploads with AI parsing
 * 
 * @param {Function} onFileParsed - Callback with parsed data
 * @param {String} uploadType - 'resume' or 'job_description'
 * @param {Function} onClose - Callback to close uploader
 */
const DragDropUploader = ({ onFileParsed, uploadType = 'resume', onClose }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fileName, setFileName] = useState('');

  const acceptedFileTypes = uploadType === 'resume' 
    ? '.pdf,.doc,.docx,.txt'
    : '.pdf,.doc,.docx,.txt';

  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const validateFile = (file) => {
    // Check file size
    if (file.size > maxFileSize) {
      setError(`File size exceeds 10MB limit. Please upload a smaller file.`);
      return false;
    }

    // Check file type
    const validTypes = ['application/pdf', 'application/msword', 
                       'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                       'text/plain'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|doc|docx|txt)$/i)) {
      setError('Invalid file type. Please upload PDF, DOC, DOCX, or TXT files.');
      return false;
    }

    return true;
  };

  const uploadAndParseFile = async (file) => {
    if (!validateFile(file)) {
      return;
    }

    setUploading(true);
    setError('');
    setFileName(file.name);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const endpoint = uploadType === 'resume' 
        ? `${backendUrl}/api/candidates/parse-resume`
        : `${backendUrl}/api/jobs/parse-job-description`;

      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          onFileParsed(data.parsed_data || data.candidate_data || data.job_data || data.data);
        }, 500);
      } else {
        setError(data.message || 'Failed to parse file');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload and parse file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      uploadAndParseFile(e.dataTransfer.files[0]);
    }
  }, [uploadType]);

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      uploadAndParseFile(e.target.files[0]);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Upload className="h-6 w-6 text-blue-600" />
              {uploadType === 'resume' ? 'Upload Resume' : 'Upload Job Description'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {uploadType === 'resume' 
                ? 'AI will automatically extract candidate information from the resume'
                : 'AI will automatically extract job details from the description'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={uploading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Drag and Drop Area */}
          <div
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {/* Upload Icon */}
            {!uploading && !success && (
              <>
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-blue-100 rounded-full">
                    <Upload className="h-12 w-12 text-blue-600" />
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-lg font-medium text-gray-900">
                    Drag and drop your file here
                  </p>
                  <p className="text-sm text-gray-600">
                    or
                  </p>
                  <label className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
                    <input
                      type="file"
                      className="hidden"
                      accept={acceptedFileTypes}
                      onChange={handleChange}
                      disabled={uploading}
                    />
                    Browse Files
                  </label>
                </div>

                <p className="text-xs text-gray-500 mt-4">
                  Supported formats: PDF, DOC, DOCX, TXT (Max 10MB)
                </p>
              </>
            )}

            {/* Uploading State */}
            {uploading && (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader className="h-12 w-12 text-blue-600 animate-spin mb-4" />
                <p className="text-lg font-medium text-gray-900">Processing {fileName}...</p>
                <p className="text-sm text-gray-600 mt-2">
                  {uploadType === 'resume' 
                    ? 'Extracting candidate information...'
                    : 'Extracting job details...'}
                </p>
              </div>
            )}

            {/* Success State */}
            {success && (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="p-4 bg-green-100 rounded-full mb-4">
                  <Check className="h-12 w-12 text-green-600" />
                </div>
                <p className="text-lg font-medium text-gray-900">
                  {uploadType === 'resume' ? 'Resume' : 'Job Description'} Parsed Successfully!
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Filling form with extracted information...
                </p>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Upload Failed</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Instructions */}
          {!uploading && !success && (
            <div className="mt-6 space-y-3">
              <h3 className="text-sm font-medium text-gray-900">What happens next:</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                {uploadType === 'resume' ? (
                  <>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600">1.</span>
                      <span>AI will extract candidate name, contact info, skills, and experience</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600">2.</span>
                      <span>The candidate creation form will be auto-filled with extracted data</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600">3.</span>
                      <span>Review and edit any information before saving</span>
                    </li>
                  </>
                ) : (
                  <>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600">1.</span>
                      <span>AI will extract job title, requirements, salary, and location</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600">2.</span>
                      <span>The job creation form will be auto-filled with extracted data</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600">3.</span>
                      <span>Review and edit any information before posting</span>
                    </li>
                  </>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={uploading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default DragDropUploader;
